import { Handle, Position } from '@xyflow/react';
import { Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { useIntlLocale, useTranslation } from '@hooks';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import {
    AUTOMATION_NODE_TYPE,
    BRANCH_KEY,
    TRIGGER_SOURCE_TYPE,
    expandTriggerIds,
    getNodeKind,
} from '@constants/automations';
import { NodeIcon } from './nodeMeta';
import { countConditions } from './conditions';
import { formatWait } from './wait';
import { describeDateTrigger } from './dateTrigger';

const FlowNode = ({ id, data, selected }) => {
    const { t } = useTranslation();
    const intlLocale = useIntlLocale();
    const kind = getNodeKind(data);

    const isTrigger = data.type === AUTOMATION_NODE_TYPE.TRIGGER;
    const isExit = data.type === AUTOMATION_NODE_TYPE.EXIT;
    const isBranch = data.type === AUTOMATION_NODE_TYPE.BRANCH;
    const deletable = !isTrigger && !isExit && !!data.onDelete;
    const issues = data.issues || [];

    const subtitle = () => {
        switch (data.type) {
            case AUTOMATION_NODE_TYPE.TRIGGER: {
                if (data.sourceType === TRIGGER_SOURCE_TYPE.DATE && data.date) {
                    return describeDateTrigger(data.date, t, intlLocale);
                }
                const eventCount = expandTriggerIds(data.activityTypes, data.integrationTypes).length;
                return eventCount
                    ? t('AutomationsPage.nodes.eventCount', { count: eventCount })
                    : t('AutomationsPage.nodes.pickEvents');
            }
            case AUTOMATION_NODE_TYPE.SEND_MESSAGE:
                if (data.isCoupon) {
                    return data.couponTemplateId
                        ? t('AutomationsPage.nodes.couponSelected')
                        : t('AutomationsPage.nodes.pickCoupon');
                }
                return data.message ? data.message.slice(0, 38) : t('AutomationsPage.nodes.noMessage');
            case AUTOMATION_NODE_TYPE.SEND_SURVEY:
                return data.surveyTemplateId
                    ? t('AutomationsPage.nodes.surveySelected')
                    : t('AutomationsPage.nodes.pickSurvey');
            case AUTOMATION_NODE_TYPE.WAIT:
                return formatWait(t, data.waitMinutes);
            case AUTOMATION_NODE_TYPE.BRANCH: {
                const conditionCount = countConditions(data.conditions);
                return conditionCount
                    ? t('AutomationsPage.nodes.conditionCount', { count: conditionCount })
                    : t('AutomationsPage.nodes.setCondition');
            }
            case AUTOMATION_NODE_TYPE.EXIT:
                return t('AutomationsPage.nodes.exitSubtitle');
            default:
                return '';
        }
    };

    return (
        <Box
            sx={{
                width: 280,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: '12px',
                background: '#fff',
                border: `1px solid ${
                    issues.length ? 'var(--error-main)' : selected ? 'var(--primary-main)' : 'var(--divider)'
                }`,
                boxShadow: selected
                    ? `0 0 0 1px ${issues.length ? 'var(--error-main)' : 'var(--primary-main)'}`
                    : '0 1px 2px rgba(0,0,0,0.06)',
                cursor: 'pointer',
                position: 'relative',
                '&:hover .node-delete': { opacity: deletable ? 1 : 0 },
            }}
        >
            {!isTrigger && <Handle type="target" position={Position.Top} style={{ background: 'var(--divider)' }} />}

            <NodeIcon kind={kind} />

            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" sx={{ lineHeight: '18px' }}>
                    {t(`AutomationsPage.nodes.${kind}`)}
                </Typography>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {subtitle()}
                </Typography>
            </Box>

            {issues.length > 0 && (
                <Tooltip
                    disableInteractive
                    placement="top"
                    title={issues.map(issue => (
                        <div key={issue}>{t(`AutomationsPage.issues.${issue}`)}</div>
                    ))}
                >
                    <ErrorRoundedIcon sx={{ fontSize: 18, color: 'var(--error-main)', flexShrink: 0 }} />
                </Tooltip>
            )}

            {deletable && (
                <Box
                    className="node-delete"
                    onClick={e => {
                        e.stopPropagation();
                        data.onDelete?.(id);
                    }}
                    sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: '#fff',
                        border: '1px solid var(--divider)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 120ms',
                    }}
                >
                    <CloseIcon sx={{ fontSize: 13, color: 'var(--text-secondary)' }} />
                </Box>
            )}

            {!isExit &&
                (isBranch ? (
                    <>
                        <Handle
                            type="source"
                            id={BRANCH_KEY.YES}
                            position={Position.Bottom}
                            style={{ left: '30%', background: '#0B8D00' }}
                        />
                        <Handle
                            type="source"
                            id={BRANCH_KEY.NO}
                            position={Position.Bottom}
                            style={{ left: '70%', background: '#B00020' }}
                        />
                    </>
                ) : (
                    <Handle type="source" position={Position.Bottom} style={{ background: 'var(--divider)' }} />
                ))}
        </Box>
    );
};

export default FlowNode;
